import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, CheckCircle, Loader2, ChevronDown } from 'lucide-react'
import { messagesApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 10

const Notifications = () => {
  usePageTitle()
  const [messages, setMessages] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [markingAll, setMarkingAll] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // 初始加载
  useEffect(() => {
    loadMessages(1, true)
  }, [])

  // 设置Intersection Observer监听滚动到底部
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => {
            const nextPage = prev + 1
            loadMessages(nextPage, false)
            return nextPage
          })
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [hasMore, loading])

  const loadMessages = async (pageNum: number, isInitial: boolean = false) => {
    if (isInitial) {
      setInitialLoading(true)
    } else {
      setLoading(true)
    }

    try {
      const resp = await messagesApi.list({ page: pageNum, page_size: PAGE_SIZE }) as any
      const items = resp?.messages || []
      const newMessages = items.map((n: any) => ({
        id: n.id,
        type: n.msg_type || 'system',
        title: n.title,
        content: n.content,
        time: n.created_at ? new Date(n.created_at) : null,
        read: n.is_read === '1',
        relatedId: n.related_id,
      }))

      setTotalPages(resp?.total_pages || 0)
      setUnreadCount(resp?.unread_count || 0)
      setHasMore(pageNum < (resp?.total_pages || 0))

      if (isInitial) {
        setMessages(newMessages)
      } else {
        setMessages((prev) => [...prev, ...newMessages])
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
      setInitialLoading(false)
    }
  }

  // 下拉刷新
  const handleRefresh = useCallback(async () => {
    setPage(1)
    await loadMessages(1, true)
  }, [])

  // 标记单条已读
  const handleRead = async (id: string) => {
    try {
      await messagesApi.markRead(id)
    } catch (e) {
      console.error(e)
    }
    setMessages((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    )
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }

  // 全部已读
  const handleMarkAllRead = async () => {
    setMarkingAll(true)
    try {
      await messagesApi.markAllRead()
      setMessages((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (e) {
      console.error(e)
    } finally {
      setMarkingAll(false)
    }
  }

  // 格式化相对时间
  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    if (minutes < 1) return '刚刚'
    if (minutes < 60) return `${minutes}分钟前`
    if (hours < 24) return `${hours}小时前`
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // 格式化日期分组标题
  const formatDateHeader = (date: Date) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const diff = today.getTime() - msgDate.getTime()
    if (diff === 0) return '今天'
    if (diff === 86400000) return '昨天'
    if (diff < 86400000 * 7) return `${Math.floor(diff / 86400000)}天前`
    return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })
  }

  // 按日期分组
  const grouped = messages.reduce<Record<string, typeof messages>>((acc, msg) => {
    if (!msg.time) return acc
    const key = msg.time.toDateString()
    if (!acc[key]) acc[key] = []
    acc[key].push(msg)
    return acc
  }, {})

  const sortedDates = Object.keys(grouped).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )

  const getIcon = (type: string) => {
    switch (type) {
      case 'points_in':
        return CheckCircle
      case 'points_out':
        return CheckCircle
      case 'exchange':
        return Bell
      case 'system':
        return Bell
      default:
        return Bell
    }
  }

  const getIconColor = (type: string, read: boolean) => {
    if (!read) {
      // 未读消息用更鲜明的颜色
      switch (type) {
        case 'points_in':
          return 'bg-green-500 text-white'
        case 'points_out':
          return 'bg-red-500 text-white'
        case 'exchange':
          return 'bg-orange-500 text-white'
        case 'system':
          return 'bg-purple-500 text-white'
        default:
          return 'bg-blue-500 text-white'
      }
    }
    // 已读消息用浅色
    switch (type) {
      case 'points_in':
        return 'bg-green-100 text-green-600'
      case 'points_out':
        return 'bg-red-100 text-red-600'
      case 'exchange':
        return 'bg-orange-100 text-orange-600'
      case 'system':
        return 'bg-purple-100 text-purple-600'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'points_in':
        return '积分到账'
      case 'points_out':
        return '积分变动'
      case 'exchange':
        return '兑换通知'
      case 'system':
        return '系统通知'
      default:
        return '通知'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* 头部 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <span className="text-white font-bold text-lg">消息通知</span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium animate-pulse">
                {unreadCount}条未读
              </span>
            )}
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markingAll}
                className="flex items-center gap-1 px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-full transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-3 h-3" />
                {markingAll ? '处理中...' : '全部已读'}
              </button>
            )}
          </div>
        </div>
        <p className="text-green-100 text-sm mt-1">
          {unreadCount > 0 ? `您有 ${unreadCount} 条未读消息` : '暂无未读消息'}
        </p>
      </div>

      {/* 消息列表 */}
      <div className="px-4 mt-4 space-y-4">
        {initialLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-16 text-center">
            <Bell className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">暂无消息</p>
            <p className="text-gray-400 text-sm mt-1">
              兑换礼品或积分变动时，这里会有通知
            </p>
          </div>
        ) : (
          <>
            {sortedDates.map((dateKey) => (
              <div key={dateKey}>
                {/* 日期分组标题 */}
                <div className="flex items-center gap-2 mb-2 mt-4">
                  <span className="text-sm font-semibold text-gray-600">
                    {formatDateHeader(grouped[dateKey][0].time)}
                  </span>
                  <span className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">
                    {grouped[dateKey].filter((m: any) => !m.read).length > 0
                      ? `${grouped[dateKey].filter((m: any) => !m.read).length}条未读`
                      : '已读'}
                  </span>
                </div>

                {/* 消息卡片 */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden divide-y divide-gray-100">
                  {grouped[dateKey].map((msg) => {
                    const Icon = getIcon(msg.type)
                    return (
                      <div
                        key={msg.id}
                        onClick={() => !msg.read && handleRead(msg.id)}
                        className={`px-4 py-3 flex items-start gap-3 cursor-pointer transition-all ${
                          !msg.read
                            ? 'bg-green-50/50 hover:bg-green-100/50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* 左侧图标 + 未读红点 */}
                        <div className="relative flex-shrink-0 mt-0.5">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center ${getIconColor(
                              msg.type,
                              msg.read
                            )}`}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          {/* 未读红点 */}
                          {!msg.read && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" />
                          )}
                        </div>

                        {/* 中间内容 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p
                              className={`text-sm font-medium line-clamp-1 ${
                                !msg.read ? 'text-gray-900 font-semibold' : 'text-gray-500'
                              }`}
                            >
                              {msg.title}
                            </p>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                !msg.read
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-100 text-gray-400'
                              }`}
                            >
                              {getTypeLabel(msg.type)}
                            </span>
                          </div>
                          <p
                            className={`text-xs mt-0.5 ${
                              !msg.read ? 'text-gray-700' : 'text-gray-400'
                            }`}
                          >
                            {msg.content}
                          </p>
                        </div>

                        {/* 右侧时间 */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-400">
                            {formatTime(msg.time)}
                          </span>
                          {!msg.read && (
                            <span className="text-xs text-green-600 font-medium">
                              新消息
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            {/* 加载更多指示器 */}
            <div ref={loadMoreRef} className="py-6">
              {loading && (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                  <span className="text-sm text-gray-500">加载中...</span>
                </div>
              )}
              {!loading && !hasMore && messages.length > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-sm">无更多通知</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                </div>
              )}
              {!loading && hasMore && (
                <div className="flex items-center justify-center gap-1 text-gray-400">
                  <ChevronDown className="w-4 h-4 animate-bounce" />
                  <span className="text-sm">下拉加载更多</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="px-4 mt-8 text-center">
        <p className="text-xs text-gray-400">
          {localStorage.getItem('copyright') || '© 2026 眉山疾控'}
        </p>
      </div>
    </div>
  )
}

export default Notifications
